'use client';

import * as React from 'react';
import { usePathname } from '@/compat/navigation';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Mic, MicOff, Video, VideoOff, PhoneOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMeetingOptional } from '@/contexts/meeting-context';
import { ConnectionState } from 'livekit-client';

export function MeetingBar() {
  const meeting = useMeetingOptional();
  const pathname = usePathname();

  const isOnMeetingsPage = pathname.includes('/meetings');

  if (!meeting?.activeMeeting || isOnMeetingsPage) return null;

  const { activeMeeting, participants, isMuted, isVideoEnabled, toggleMute, toggleVideo, leaveMeeting, connectionState } = meeting;

  const isConnecting = connectionState === ConnectionState.Connecting;
  const isConnected = connectionState === ConnectionState.Connected;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-background/95 backdrop-blur-lg border shadow-lg">
        <div className="flex items-center gap-2">
          <div className={cn(
            'w-2 h-2 rounded-full',
            isConnected && 'bg-green-500',
            isConnecting && 'bg-yellow-500 animate-pulse',
            !isConnected && !isConnecting && 'bg-red-500'
          )} />
          <span className="text-sm font-medium">{activeMeeting.roomName}</span>
        </div>

        <div className="w-px h-6 bg-border" />

        <div className="flex -space-x-2">
          {participants.slice(0, 4).map((p) => (
            <Tooltip key={p.identity}>
              <TooltipTrigger asChild>
                <div className="relative">
                  <Avatar className={cn(
                    'h-8 w-8 border-2 border-background',
                    p.isSpeaking && 'ring-2 ring-green-500'
                  )}>
                    <AvatarFallback className="text-xs">
                      {p.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {p.isMuted && (
                    <div className="absolute -bottom-0.5 -right-0.5 bg-destructive rounded-full p-0.5">
                      <MicOff className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{p.name} {p.isLocal && '(You)'}</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {participants.length > 4 && (
            <div className="h-8 w-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
              +{participants.length - 4}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-border" />

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isMuted ? 'destructive' : 'ghost'}
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {isMuted ? 'Unmute' : 'Mute'}
            </TooltipContent>
          </Tooltip>

          {activeMeeting.type === 'video' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={!isVideoEnabled ? 'destructive' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={toggleVideo}
                >
                  {isVideoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={leaveMeeting}
              >
                <PhoneOff className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              Leave meeting
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
